<script>
import {mapActions, mapGetters, mapState} from "vuex";
import RightColumnGroup from "@/components/layout/RightColumnGroup.vue";
import MaterialListSheet from "@/components/ui/Bildungsportal/MaterialListSheet.vue";
import { metricEventTypes, metricParents } from "@/lib";
import Breadcrumbs from "@/components/ui/Breadcrumbs.vue";

export default {
  name: "BildungsMaterialList",
  components: {MaterialListSheet, RightColumnGroup, Breadcrumbs},
  methods: {
    ...mapActions("bildungsportal", ["loadMaterialien", "loadContactPersons", "loadMenuItems"]),
    ...mapActions("user", ["setShowBildungsportal"]),
    ...mapActions("metrics", ["postHeartbeat"]),
  },
  async mounted() {
    if(!this.title) {
     await this.loadMenuItems();
    }
    await this.postHeartbeat({
      name: this.title.name,
      type: metricEventTypes.PAGE_VISIT,
      parent: metricParents.BILDUNGSPORTAL,
      data: {
        bildungsportalMetric: true,
      }
    })
    if(this.contactPersons.length === 0) {
      await this.loadContactPersons();
    }
    await this.loadMaterialien(this.$route.params.id);
  },
  data: () => ({
    baseUrl: process.env.VUE_APP_API_BASE_URL,
    selectedThema: "Alle",
    selectedJahrgang: undefined,
  }),
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id) {
        this.loadMaterialien(to.params.id);
        this.selectedThema = "Alle";
      }
    }
  },
  computed: {
    ...mapState("bildungsportal", ["materialien", "contactPersons"]),
    ...mapGetters("user", ["showBildungsportal"]),
    typeId() {
      return this.$route.params.id;
    },
    jahrgangsstufen() {
      const jahrgangSet = new Set();

      for (const material of this.materialien) {
        for (const jahrgang of material.Jahrgangsstufen) {
          jahrgangSet.add(jahrgang.Bezeichnung);
        }
      }

      return Array.from(jahrgangSet).sort((a, b) => a.localeCompare(b));
    },
    themen() {
      const themaCounts = {};

      for (const material of this.materialien) {

        for (const thema of material.Themas) {
          const name = thema.Name;
          themaCounts[name] = (themaCounts[name] || 0) + 1;
        }
      }

      return [ { name: "Alle", count: this.materialien.length },
          ...Object.entries(themaCounts)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([name, count]) => ({ name, count }))
      ];
    },
    filteredItems() {
      const items =  this.materialien.filter(material => {
        const matchesThema = this.selectedThema === "Alle" || material.Themas.some(t => t.Name === this.selectedThema);
        const matchesJahrgang = !this.selectedJahrgang || material.Jahrgangsstufen.some(j => j.Bezeichnung === this.selectedJahrgang);
        return matchesThema && matchesJahrgang;
      });

      if(this.title !== "Veranstaltungen") return items;

      items.sort((a, b) => new Date(a.Datum ?? 0) - new Date(b.Datum ?? 0));

      return items;
    },
    title() {
      return this.$store.getters["bildungsportal/getMenuItemById"](parseInt(this.typeId, 10));
    }
  }
}
</script>

<template>
<v-row no-gutters class="min-height-100">
  <v-col cols="12" md="8" class="new-wrapper py-10 px-5" v-if="title">
    <breadcrumbs />
    <div class="custom-header-text new-font-bold mb-5">{{ title.name }}</div>
    <p style="white-space: pre-line;">
      {{ title.text }}
    </p>
    <div class="d-flex flex-row align-center mb-4 justify-space-between">
      <b>Inhalte eingrenzen</b>
      <div style="width: 250px;">
      <v-select :items="jahrgangsstufen"
                placeholder="Altersgruppe auswählen"
                single-line hide-details filled dense
                v-model="selectedJahrgang"
                color="primary"
                background-color="rgb(235, 217, 227)"
                />
      </div>

    </div>
    <div class="pb-5">
      <v-btn v-for="thema of themen"
             :key="thema.name"
             @click="selectedThema = thema.name"
             :color="selectedThema === thema.name ? 'primary' : ''"
             outlined
             small
             :class="selectedThema === thema.name ? 'active-thema-button' : 'inactive-thema-button'"
             class="mr-2 mb-2">{{ thema.name }}
          <v-chip class="font-weight-bold"
                  :class="selectedThema === thema.name ? 'active-thema-badge primary-text' : ''"
                  :color="selectedThema === thema.name ? 'rgb(222,185,203)' : 'rgb(220, 221, 224)'"
                  style="margin-left: 15px" x-small>{{ thema.count }}</v-chip>
      </v-btn>
    </div>
    <material-list-sheet v-for="material of filteredItems"
                         :key="material.id"
                         :material="material" />
  </v-col>
  <v-col
      cols="12" md="4"
      class="contact_container pl-4"
  >
    <RightColumnGroup :content-object="{ personsInCharge: contactPersons }"  v-if="contactPersons.length > 0"/>
  </v-col>
</v-row>
</template>

<style scoped>
.material-preview {
  display: flex;
  flex-direction: row;
}



.active-thema-button {
  background: rgb(229,206,218);
  border-color: #9C0047 !important;
}

.inactive-thema-button {
  border-color: #1F2B374D !important;
}

.active-thema-badge {
  background: rgb(222,185,203);
}



</style>
